import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useConnection, useReadContracts } from 'wagmi';
import { chewyLpAbi } from "@/constants/abi/chewyLpAbi";
import { type Address, formatUnits } from 'viem';
import { chainSettings, type ChainType, fallbackChainId, fallbackMulticall } from '@/constants/chain-specific/chains';
import { getTokenInfo } from '@/lib/getTokenInfo';
interface AllowanceData {
  bigInt: bigint;
  number: number;
}
interface TokenData {
  balance: AllowanceData;
  isLp: boolean;
  permit: {
    domainSeparator: string;
    typehash: string;
    nonces: bigint;
  };
  symbol: string;
  name: string;
  decimals: number;
  gt_score?: number;
  logo?: string;
  website?: string;
  telegram?: string;
  twitter_handle?: string;
}
interface UseAllowanceReturn {
  allowance: AllowanceData;
  token: TokenData;
  refresh: () => void;
}
export function useSearchToken(tokenAddress, t0, spenderAddress) {
  const $ = _c(63);
  const chainId = t0 === undefined ? fallbackChainId : t0;
  const {
    address
  } = useConnection();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = BigInt(0);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [allowanceBigInt] = useState(t1);
  const [allowanceNumber] = useState(0);
  const [decimals, setDecimals] = useState(18);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = BigInt(0);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [balanceBigInt, setBalanceBigInt] = useState(t2);
  const [balanceNumber, setBalanceNumber] = useState(0);
  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [isLp, setIsLp] = useState(false);
  const [domainSeparator] = useState("");
  const [permitTypehash] = useState("");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = BigInt(0);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [nonces] = useState(t3);
  const [gtScore, setGtScore] = useState(undefined);
  const [logo, setLogo] = useState(undefined);
  const [website, setWebsite] = useState(undefined);
  const [telegram, setTelegram] = useState(undefined);
  const [twitterHandle, setTwitterHandle] = useState(undefined);
  let t4;
  if ($[3] !== chainId || $[4] !== tokenAddress) {
    t4 = {
      abi: chewyLpAbi,
      address: tokenAddress,
      chainId
    };
    $[3] = chainId;
    $[4] = tokenAddress;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const tokenContract = t4;
  const t5 = !!address;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      enabled: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== tokenContract) {
    t7 = {
      ...tokenContract,
      functionName: "decimals"
    };
    $[8] = tokenContract;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = address as Address;
  let t9;
  if ($[10] !== t8) {
    t9 = [t8];
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t9 || $[13] !== tokenContract) {
    t10 = {
      ...tokenContract,
      functionName: "balanceOf",
      args: t9
    };
    $[12] = t9;
    $[13] = tokenContract;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  let t12;
  if ($[15] !== tokenContract) {
    t11 = {
      ...tokenContract,
      functionName: "symbol"
    };
    t12 = {
      ...tokenContract,
      functionName: "name"
    };
    $[15] = tokenContract;
    $[16] = t11;
    $[17] = t12;
  } else {
    t11 = $[16];
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t7) {
    t13 = [t7, t10, t11, t12];
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t7;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall;
  let t15;
  if ($[23] !== t13 || $[24] !== t14 || $[25] !== t6) {
    t15 = {
      allowFailure: false,
      query: t6,
      contracts: t13,
      multicallAddress: t14
    };
    $[23] = t13;
    $[24] = t14;
    $[25] = t6;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const {
    data: tokenData,
    refetch
  } = useReadContracts(t15);
  let t16;
  if ($[27] !== refetch) {
    t16 = () => {
      refetch();
    };
    $[27] = refetch;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const refresh = t16;
  let t17;
  let t18;
  if ($[29] !== address || $[30] !== tokenData) {
    t17 = () => {
      if (!address) {
        return;
      }
      if (tokenData) {
        setDecimals(tokenData[0]);
        setBalanceBigInt(tokenData[1]);
        setBalanceNumber(Number(formatUnits(tokenData[1], tokenData[0])));
        setSymbol(tokenData[2]);
        setName(tokenData[3]);
        setIsLp(typeof tokenData[3] === "string" && tokenData[3].toLowerCase().includes("lp"));
      }
    };
    t18 = [address, tokenData];
    $[29] = address;
    $[30] = tokenData;
    $[31] = t17;
    $[32] = t18;
  } else {
    t17 = $[31];
    t18 = $[32];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[33] !== chainId || $[34] !== tokenAddress) {
    t19 = () => {
      const fetchAdditionalData = async () => {
        if (!tokenAddress) {
          return;
        }
        const additionalData = await getTokenInfo(chainId, tokenAddress);
        if (additionalData) {
          setGtScore(additionalData.gt_score);
          setLogo(additionalData.logo !== "missing.png" ? additionalData.logo : undefined);
          setWebsite(additionalData.website);
          setTelegram(additionalData.telegram);
          setTwitterHandle(additionalData.twitter_handle);
        }
      };
      fetchAdditionalData();
    };
    t20 = [tokenAddress, chainId];
    $[33] = chainId;
    $[34] = tokenAddress;
    $[35] = t19;
    $[36] = t20;
  } else {
    t19 = $[35];
    t20 = $[36];
  }
  useEffect(t19, t20);
  let t21;
  if ($[37] !== allowanceBigInt || $[38] !== allowanceNumber) {
    t21 = {
      bigInt: allowanceBigInt,
      number: allowanceNumber
    };
    $[37] = allowanceBigInt;
    $[38] = allowanceNumber;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== balanceBigInt || $[41] !== balanceNumber) {
    t22 = {
      bigInt: balanceBigInt,
      number: balanceNumber
    };
    $[40] = balanceBigInt;
    $[41] = balanceNumber;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== domainSeparator || $[44] !== nonces || $[45] !== permitTypehash) {
    t23 = {
      domainSeparator,
      typehash: permitTypehash,
      nonces
    };
    $[43] = domainSeparator;
    $[44] = nonces;
    $[45] = permitTypehash;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== decimals || $[48] !== gtScore || $[49] !== isLp || $[50] !== logo || $[51] !== name || $[52] !== symbol || $[53] !== t22 || $[54] !== t23 || $[55] !== telegram || $[56] !== twitterHandle || $[57] !== website) {
    t24 = {
      balance: t22,
      isLp,
      permit: t23,
      symbol,
      name,
      decimals,
      gt_score: gtScore,
      logo,
      website,
      telegram,
      twitter_handle: twitterHandle
    };
    $[47] = decimals;
    $[48] = gtScore;
    $[49] = isLp;
    $[50] = logo;
    $[51] = name;
    $[52] = symbol;
    $[53] = t22;
    $[54] = t23;
    $[55] = telegram;
    $[56] = twitterHandle;
    $[57] = website;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== refresh || $[60] !== t21 || $[61] !== t24) {
    t25 = {
      allowance: t21,
      token: t24,
      refresh
    };
    $[59] = refresh;
    $[60] = t21;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
}